import { download } from './download'

export interface Zipper {
    addFile: (filename: string, data: Uint8Array) => void
    generate: () => Promise<void>
}

// TODO take onError instead?
export type ZipperFactory = (outputFilename: string, onDone: () => void) => Zipper

const defaultZipperFactory: ZipperFactory = (outputFilename, onDone) => {
    const worker = new Worker('workers/zip.js')
    let resolvePromise: (() => void) | null = null

    // Must be called on both success and failure
    const markDone = () => {
        if (resolvePromise) {
            resolvePromise()
            resolvePromise = null
        }

        worker.terminate()
        onDone()
    }

    let anyErrors = false

    worker.onerror = (errEvent) => {
        anyErrors = true
        markDone()

        const error = new Error(errEvent.message)

        // A bit hacky - overwrite the stack to include info from the event
        error.stack =
            'Error: ' +
            errEvent.message +
            '\n' +
            '  at (' +
            errEvent.filename +
            ':' +
            errEvent.lineno +
            ':' +
            errEvent.colno +
            ')'

        throw error
    }

    worker.onmessage = (evt: MessageEvent) => {
        const data = evt.data as { type: string; message?: string; stack?: string }

        if (data.type === 'error') {
            anyErrors = true
            const error = new Error(data.message)
            error.stack = data.stack
            throw error
        } else if (data.type === 'done') {
            if (anyErrors) {
                return
            }

            const { url } = evt.data as { url: string }
            download(url, outputFilename)
            markDone()
        } else {
            throw new Error('Unrecognized message type: ' + data.type)
        }
    }

    return {
        addFile: (filename, data) =>
            worker.postMessage({ type: 'addFile', filename, contents: data.buffer }, [
                data.buffer as ArrayBuffer,
            ]),
        generate: async () => {
            if (anyErrors) {
                return
            }

            worker.postMessage({ type: 'generate' })
            return new Promise<void>((resolve) => {
                resolvePromise = resolve
            })
        },
    }
}

let zipperFactory: ZipperFactory = defaultZipperFactory

// Export in a wrapper so we can swap out the implementation
export const createZipper: ZipperFactory = (outputFilename, onDone) => {
    return zipperFactory(outputFilename, onDone)
}

// For tests
export const setZipperFactory = (
    factory: ZipperFactory,
    onReset: (callback: () => void) => void,
) => {
    zipperFactory = factory
    onReset(() => {
        zipperFactory = defaultZipperFactory
    })
}
